import { action, computed, makeObservable, observable, runInAction } from "mobx";
import {
    GetVotingInformationBasis,
    GetVotingInformationBasisVariables
} from "src/api/generated/GetVotingInformationBasis";
import {
    InsertVotingInformationBasis,
    InsertVotingInformationBasisVariables
} from "src/api/generated/InsertVotingInformationBasis";
import {
    DELETE_VOTING,
    GET_VOTING_DETAILS,
    GET_VOTING_INFORMATION_BASIS,
    GET_VOTING_OPTIONS,
    GET_VOTING_TENANTS_OPTIONS_RESULTS,
    GET_VOTINGS_BY_AGENDAITEMID,
    INSERT_VOTING_INFORMATION_BASIS,
    INSERT_VOTING_OPTION_AGENDAITEM,
    UPDATE_VOTETYPE_VOTINGS,
    UPSERT_VOTING_AGENDA_RESULTS
} from "src/api/voting";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { RootStore } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import { v4 as uuidv4 } from "uuid";
import i18next from "i18next";
import { InsertVotingOptions, InsertVotingOptionsVariables } from "src/api/generated/InsertVotingOptions";
import { DeleteVoting, DeleteVotingVariables } from "src/api/generated/DeleteVoting";
import {
    GetVotingsByAgendaitemId,
    GetVotingsByAgendaitemIdVariables
} from "src/api/generated/GetVotingsByAgendaitemId";
import {
    GetVotingTenantsAndOptionsAndResults,
    GetVotingTenantsAndOptionsAndResultsVariables
} from "src/api/generated/GetVotingTenantsAndOptionsAndResults";
import {
    UpsertVotingAgendaitemResult,
    UpsertVotingAgendaitemResultVariables
} from "src/api/generated/UpsertVotingAgendaitemResult";
import { GetVotingDetails, GetVotingDetailsVariables } from "src/api/generated/GetVotingDetails";
import { GetVotingOptions, GetVotingOptionsVariables } from "src/api/generated/GetVotingOptions";
import { survey_voting_mls_constraint, survey_voting_mls_update_column } from "src/api/generated/globalTypes";
import { IAttendanceState } from "./AttendanceListStore";
import { UpdateVotetypeVotings, UpdateVotetypeVotingsVariables } from "src/api/generated/UpdateVotetypeVotings";

interface IQuorums {
    key: number;
    label: string;
}

interface IKeys {
    keyid: string;
    name: string;
}

export enum QuorumType {
    SIMPLE_MAJORITY = 0,
    QUALIFIED_MAJORITY = 1,
    UNANIMITY = 2
}

export enum VoteType {
    ARBITRAMENT = 0,
    SELECTION_VOTE = 1
}
export interface IVotingBasis {
    votingId: string;
    title: string;
    question: string;
    quorum: QuorumType;
    key1: string;
    key2?: string;
    veto: boolean;
    votingMlsId: string;
    votetype: number;
}

export interface IVoting {
    votingid: string;
    votingagendaitemid: string;
    question: string;
    title: string;
}

export interface IVotingTenant {
    tenantid: string;
    name: string;
    unitvalue: number;
}

export interface IVotingOptions {
    votingoptionid: string;
    text: string;
}

export interface IVotingResults {
    id: string;
    state: number;
    unitid: string;
    votingagendaitemrealestateid: string;
    tenantid: string;
    votingoptionid: string | null;
}

export interface IPieChartProps {
    label: string;
    color: string;
    number: any;
}

export class VotingStore {
    rootStore: RootStore;
    isVotingModalDisplayed: boolean = false;

    quorums: IQuorums[] = [];
    keys: IKeys[] = [];

    votingBasis: IVotingBasis = {
        votingId: "",
        title: "",
        question: "",
        quorum: QuorumType.SIMPLE_MAJORITY,
        key1: "",
        key2: "",
        veto: false,
        votingMlsId: "",
        votetype: VoteType.ARBITRAMENT
    };

    votingList: IVoting[] = [];
    selectedVoting: IVoting = {
        votingid: "",
        votingagendaitemid: "",
        title: "",
        question: ""
    };

    votingOptionsDefault: string[] = [i18next.t("labels.yes"), i18next.t("labels.no"), i18next.t("labels.abstrain")];
    selectedRealestateIds: string[] = [];

    lastLoadedAgendaitemId: string | null = "";
    lastLoadedVotingId: string = "";

    votingTenants: IVotingTenant[] = [];
    votingOptions: IVotingOptions[] = [];
    votingResults: IVotingResults[] = [];

    votingComponentResponse: { id: string; selectedColumn: number | null }[] = [];

    isNewVoting: boolean = true;
    selectedVotingId: string = "";
    votingColors: string[] = ["#004997", "#f8a300", "#900000", "#194d00", "#650097"];
    quorumMls: string = "";
    key1Mls: string = "";
    key2Mls: string = "";
    isVotingResultTie: boolean = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isVotingModalDisplayed: observable,
            quorums: observable,
            keys: observable,
            votingBasis: observable,
            votingOptionsDefault: observable,
            selectedRealestateIds: observable,
            votingList: observable,
            selectedVoting: observable,
            lastLoadedAgendaitemId: observable,
            lastLoadedVotingId: observable,
            votingTenants: observable,
            votingOptions: observable,
            votingComponentResponse: observable,
            votingResults: observable,
            isNewVoting: observable,
            selectedVotingId: observable,
            votingColors: observable,
            quorumMls: observable,
            key1Mls: observable,
            key2Mls: observable,
            isVotingResultTie: observable,

            hasVotingResultChanged: computed,
            votingResultText: computed,
            isQuorate: computed,
            pieChartList: computed,
            pieChartOuterList: computed,
            votingColorResult: computed,
            isTitleAndQuestionFilledOut: computed,
            isRealestateSelected: computed,
            tenantRows: computed,

            loadVotingInformationBasis: action,
            setIsVotingModalDisplayed: action,
            setQuorums: action,
            setKeys: action,
            updateQuorum: action,
            updateKey: action,
            updateVeto: action,
            updateVotingBasisField: action,
            resetVotingBasis: action,
            saveVotingInformationBasis: action,
            setVotingOptionsDefault: action,
            setVotingOptions: action,
            setSelectedRealestateIds: action,
            saveVotingOptions: action,
            deleteVotingById: action,
            resetAllVotingData: action,
            loadAllVotingsByAgendaItemId: action,
            setSelectedVoting: action,
            loadVotingTenantsAndOptions: action,
            saveVotingResult: action,
            setVotingComponentResponse: action,
            setIsNewVoting: action,
            loadVotingDetails: action,
            setSelectedVotingId: action,
            loadVotingOptions: action,
            setQuorumAndKeysMls: action,
            setIsVotingResultTie: action
        });
    }

    /* COMPUTED */

    get tenantRows(): {
        id: string;
        label: string;
        selectedRow: number;
        active: boolean;
    }[] {
        return this.votingTenants.map((tenant) => {
            const resp = this.votingComponentResponse.find((r) => r.id === tenant.tenantid);
            const attendance = this.rootStore.attendanceListStore.assemblyAttendances.find(
                (a) => a.tenantid === tenant.tenantid
            );
            let active = attendance ? attendance.attendancestate !== IAttendanceState.ABSENT : true;
            if (this.votingBasis.quorum === QuorumType.UNANIMITY && !this.isQuorate) {
                active = false;
            }

            return {
                id: tenant.tenantid,
                label: tenant.name,
                selectedRow: resp?.selectedColumn ?? 0,
                active
            };
        });
    }

    get hasVotingResultChanged(): boolean {
        return this.votingComponentResponse.some(({ id, selectedColumn }) => {
            const original = this.votingResults.find((r) => r.tenantid === id);
            const originalOptionId = original?.votingoptionid;
            const originalIndex = originalOptionId
                ? this.votingOptions.findIndex((opt) => opt.votingoptionid === originalOptionId)
                : -1;

            const originalSelected = originalIndex >= 0 ? originalIndex + 1 : null;

            return originalSelected !== selectedColumn;
        });
    }

    get calculateVotingResultList(): {
        tenantid: string;
        votingoptionid: string;
        unitvalue: number;
    }[] {
        const resultMap = new Map<string, { tenantid: string; votingoptionid: string; unitvalue: number }>();

        this.votingResults.forEach(({ tenantid, votingoptionid }) => {
            if (tenantid != null && !resultMap.has(tenantid)) {
                // find matching tenant to get its unitvalue
                const tenant = this.votingTenants.find((t) => t.tenantid === tenantid);
                const unitvalue = tenant?.unitvalue ?? 0;

                resultMap.set(tenantid, {
                    tenantid,
                    votingoptionid: votingoptionid ?? "",
                    unitvalue
                });
            }
        });

        return Array.from(resultMap.values());
    }

    get votingResultText(): string {
        this.setIsVotingResultTie(false);
        if (this.votingResults.every((r) => r.votingoptionid === null)) {
            if (this.votingBasis.quorum === QuorumType.UNANIMITY && !this.isQuorate) {
                return i18next.t("screens.assemblies.vote.not_quorate");
            }
            return "";
        }
        switch (this.votingBasis.quorum) {
            case QuorumType.SIMPLE_MAJORITY:
                return this.calculateQuorumSimpleMajority();
            case QuorumType.QUALIFIED_MAJORITY:
                return this.calculateQuorumQualifiedMajority();
            case QuorumType.UNANIMITY:
                return this.calculateQuorumUnanimity();
            default:
                return "";
        }
    }

    get isTitleAndQuestionFilledOut(): boolean {
        return this.votingBasis.title.length > 0 && this.votingBasis.question.length > 0;
    }

    get isRealestateSelected(): boolean {
        return this.selectedRealestateIds.length > 0;
    }

    private isWeightedKey(key: string): boolean {
        return key === this.keys[0].keyid || key === this.keys[3].keyid;
    }

    private buildTotals(key: string): Record<string, number> {
        const totals: Record<string, number> = {};
        if (this.isWeightedKey(key)) {
            // weighted by unitvalue
            for (const { votingoptionid, unitvalue } of this.calculateVotingResultList) {
                totals[votingoptionid] = (totals[votingoptionid] || 0) + unitvalue;
            }
        } else {
            // one‐tenant one‐vote (or any other unweighted)
            for (const { votingoptionid } of this.calculateVotingResultList) {
                totals[votingoptionid] = (totals[votingoptionid] || 0) + 1;
            }
        }
        return totals;
    }

    private findWinnerId(key: string): string | null {
        const totals = this.buildTotals(key);
        const entries = Object.entries(totals);
        if (entries.length === 0) return null;

        // find the max value/count
        const max = Math.max(...entries.map(([, v]) => v));

        // see how many hit that max
        const top = entries.filter(([, v]) => v === max);
        if (top.length > 1) {
            this.setIsVotingResultTie(true);
            return null;
        }
        return top[0][0];
    }

    private resolveText(optionId: string | null): string {
        if (!optionId) {
            // if we got here because of an explicit tie, the tie‐handler has already been called
            return i18next.t("screens.assemblies.vote.tie");
        }
        const opt = this.votingOptions.find((o) => o.votingoptionid === optionId);
        return opt?.text ?? "";
    }

    calculateQuorumSimpleMajority(): string {
        const key = this.votingBasis.key1;
        const winnerId = this.findWinnerId(key);
        return this.resolveText(winnerId);
    }

    calculateQuorumQualifiedMajority(): string {
        if (!this.isQuorate) {
            return i18next.t("screens.assemblies.vote.not_quorate");
        }
        if (this.calculateVotingResultList.length === 0) {
            return "";
        }

        const win1 = this.findWinnerId(this.votingBasis.key1);
        const win2 = this.findWinnerId(this.votingBasis.key2 ?? "");

        // if either is null (tie) or they differ -> overall tie
        if (!win1 || !win2 || win1 !== win2) {
            this.setIsVotingResultTie(true);
            return i18next.t("screens.assemblies.vote.tie");
        }
        return this.resolveText(win1);
    }

    getQualifiedMajorityHtmlForKey(keyId: string, mls: string): string {
        if (!this.isQuorate || this.calculateVotingResultList.length === 0) {
            return "";
        }

        // run the same winner logic you already have
        const win = this.findWinnerId(keyId);

        // pick translation key
        const tKey = win
            ? "screens.assemblies.vote.required_majority"
            : "screens.assemblies.vote.not_required_majority";

        // return the HTML string with the <strong>{{keys}}</strong> in it
        return i18next.t(tKey, { keys: mls });
    }

    calculateQuorumUnanimity = (): string => {
        if (!this.isQuorate) {
            return i18next.t("screens.assemblies.vote.not_quorate");
        }
        if (this.calculateVotingResultList.length === 0) {
            return "";
        }
        const uniqueIds = Array.from(new Set(this.calculateVotingResultList.map((r) => r.votingoptionid)));

        // tie if more than one distinct choice
        if (uniqueIds.length > 1) {
            this.setIsVotingResultTie(true);
            return i18next.t("screens.assemblies.vote.tie");
        }

        // 5) unanimous: find the single option’s text
        const winningId = uniqueIds[0];
        const winningOpt = this.votingOptions.find((opt) => opt.votingoptionid === winningId);
        return winningOpt?.text ?? "";
    };

    get isQuorate(): boolean {
        switch (this.votingBasis.quorum) {
            case QuorumType.SIMPLE_MAJORITY:
            case QuorumType.QUALIFIED_MAJORITY:
                return (
                    this.rootStore.attendanceListStore.quorumHeadInfo.isHeadQuorumSatisfied &&
                    this.rootStore.attendanceListStore.quorumValueQuotaInfo.isValueQuotaSatisfied
                );
            case QuorumType.UNANIMITY:
                return (
                    this.rootStore.attendanceListStore.quorumValueQuotaInfo.totalValueQuota ===
                    this.rootStore.attendanceListStore.quorumValueQuotaInfo.presentValueQuota
                );
            default:
                return false;
        }
    }

    private buildPieChartList(basisKey: string): IPieChartProps[] {
        // don’t run until both keys and options are loaded
        if (this.keys.length < 4 || this.votingOptions.length === 0) {
            return [];
        }

        // decide weighted vs. simple‐count mode
        const keyIds = this.keys.map((k) => k.keyid);
        const isWeighted = basisKey === keyIds[0] || basisKey === keyIds[3];

        // pre‐build a tenant -> unitvalue map
        const unitMap = isWeighted ? new Map(this.votingTenants.map((t) => [t.tenantid, t.unitvalue])) : undefined;

        // accumulate totals per option
        const totals = new Array(this.votingOptions.length).fill(0);
        for (const { id, selectedColumn } of this.votingComponentResponse) {
            if (!selectedColumn || selectedColumn < 1) continue;
            const idx = selectedColumn - 1;
            if (idx >= totals.length) continue;

            totals[idx] += isWeighted
                ? unitMap!.get(id) ?? 0 // weighted by unitvalue
                : 1; // simple one‐vote
        }

        // map into IPieChartProps
        return this.votingOptions.map((opt, i) => ({
            label: opt.text,
            color: this.votingColors?.[i] ?? "#ccc",
            number: totals[i]
        }));
    }

    /** one‐tenant–one‐vote or weighted by key1 */
    get pieChartList(): IPieChartProps[] {
        return this.buildPieChartList(this.votingBasis.key1);
    }

    /** same logic but use key2 as the basis */
    get pieChartOuterList(): IPieChartProps[] {
        return this.buildPieChartList(this.votingBasis.key2!);
    }

    get votingColorResult(): string {
        if (this.votingResults.length === 0) {
            return "#ccc";
        }
        const winningOptionId = this.votingResultText;
        const winningOption = this.votingOptions.find((opt) => opt.text === winningOptionId);
        if (winningOption) {
            const index = this.votingOptions.findIndex((opt) => opt.votingoptionid === winningOption.votingoptionid);
            return this.votingColors[index % this.votingColors.length]; // cycle through colors if more options than colors
        }

        return "#ccc";
    }

    /* ACTIONS */

    loadVotingDetails = async (votingId: string, agendaitemId: string) => {
        try {
            const { data }: { data: GetVotingDetails } = await apolloClientInstance.query<
                GetVotingDetails,
                GetVotingDetailsVariables
            >({
                query: GET_VOTING_DETAILS,
                fetchPolicy: "no-cache",
                variables: {
                    agendaitem: agendaitemId,
                    votingid: votingId
                }
            });

            runInAction(() => {
                if (data.survey_votings && data.survey_votings.length > 0) {
                    this.updateVotingBasisField("key1", data.survey_votings[0].key1id);
                    this.updateVotingBasisField("key2", data.survey_votings[0].key2id);
                    this.updateVotingBasisField("quorum", data.survey_votings[0].quorum);
                    this.updateVeto(data.survey_votings[0].veto || false);
                    this.updateVotingBasisField("votetype", data.survey_votings[0].votetype || VoteType.ARBITRAMENT);

                    if (data.survey_votings[0].voting_mls && data.survey_votings[0].voting_mls.length > 0) {
                        this.updateVotingBasisField("votingMlsId", data.survey_votings[0].voting_mls[0].id);
                    }

                    if (
                        data.survey_votings[0].votingagendaitems &&
                        data.survey_votings[0].votingagendaitems.length > 0 &&
                        data.survey_votings[0].votingagendaitems[0].votingagendaitemrealestates
                    ) {
                        const realestateIds =
                            data.survey_votings[0].votingagendaitems[0].votingagendaitemrealestates.map(
                                (r) => r.realestateid
                            );

                        this.setSelectedRealestateIds(realestateIds);
                    }
                    this.setQuorumAndKeysMls(
                        data.survey_votings[0].quorum,
                        data.survey_votings[0].key1id,
                        data.survey_votings[0].key2id
                    );
                }
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadVotingTenantsAndOptions = async () => {
        if (this.lastLoadedVotingId === this.selectedVoting.votingid) return;
        this.lastLoadedVotingId = this.selectedVoting.votingid;

        try {
            const { data }: { data: GetVotingTenantsAndOptionsAndResults } = await apolloClientInstance.query<
                GetVotingTenantsAndOptionsAndResults,
                GetVotingTenantsAndOptionsAndResultsVariables
            >({
                query: GET_VOTING_TENANTS_OPTIONS_RESULTS,
                fetchPolicy: "no-cache",
                variables: {
                    votingagendaitemid: this.selectedVoting.votingagendaitemid,
                    votingid: this.selectedVoting.votingid
                }
            });

            runInAction(() => {
                this.votingTenants = data.survey_v_votingtenantdetails.map((tenant) => ({
                    tenantid: tenant.tenantid ?? "",
                    name: tenant.name ?? "",
                    unitvalue: tenant.unitvalue ?? 0
                }));

                this.votingOptions = data.survey_votingoptions
                    .filter((option) => option.votingoption_mls.length > 0) // only keep items with at least one mls entry
                    .map((option) => {
                        const mls = option.votingoption_mls[0];
                        return {
                            votingoptionid: mls.votingoptionid ?? "",
                            text: mls.text
                        };
                    });

                this.votingResults = data.survey_votingagendaitemresults.map((result) => ({
                    id: result.id ?? "",
                    state: result.state ?? 0,
                    tenantid: result.tenantid ?? "",
                    unitid: result.unitid ?? "",
                    votingagendaitemrealestateid: result.votingagendaitemrealestateid ?? "",
                    votingoptionid: result.votingoptionid
                }));
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadVotingOptions = async () => {
        try {
            const { data }: { data: GetVotingOptions } = await apolloClientInstance.query<
                GetVotingOptions,
                GetVotingOptionsVariables
            >({
                query: GET_VOTING_OPTIONS,
                fetchPolicy: "no-cache",
                variables: {
                    votingid: this.selectedVotingId
                }
            });

            runInAction(() => {
                this.votingOptions = data.survey_votingoptions
                    .filter((option) => option.votingoption_mls.length > 0) // only keep items with at least one mls entry
                    .map((option) => {
                        const mls = option.votingoption_mls[0];
                        return {
                            votingoptionid: mls.votingoptionid ?? "",
                            text: mls.text
                        };
                    });
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadAllVotingsByAgendaItemId = async (agendaitemId: string) => {
        if (this.lastLoadedAgendaitemId === agendaitemId) return;
        this.lastLoadedAgendaitemId = agendaitemId;

        try {
            const { data }: { data: GetVotingsByAgendaitemId } = await apolloClientInstance.query<
                GetVotingsByAgendaitemId,
                GetVotingsByAgendaitemIdVariables
            >({
                query: GET_VOTINGS_BY_AGENDAITEMID,
                fetchPolicy: "no-cache",
                variables: {
                    agendaitemid: agendaitemId
                }
            });

            runInAction(() => {
                this.votingList = data.survey_v_votings_by_agendaitem.map((v) => ({
                    votingid: v.votingid,
                    votingagendaitemid: v.votingagendaitemid,
                    question: v.question ?? "",
                    title: v.title ?? ""
                }));

                if (this.votingList.length > 0) {
                    this.setSelectedVoting(this.votingList[0]);

                    // this.loadVotingTenantsAndOptions();
                }
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadVotingInformationBasis = async () => {
        try {
            const { data }: { data: GetVotingInformationBasis } = await apolloClientInstance.query<
                GetVotingInformationBasis,
                GetVotingInformationBasisVariables
            >({
                query: GET_VOTING_INFORMATION_BASIS,
                fetchPolicy: "no-cache"
            });

            runInAction(() => {
                const validQuorums: IQuorums[] = data.common_enumerations.map((item) => ({
                    key: item.key ?? -1,
                    label: item.label ?? ""
                }));

                const validKeys: IKeys[] = data.common_key_mls.map((item) => ({
                    keyid: item.keyid ?? "",
                    name: item.name ?? ""
                }));

                this.setQuorums(validQuorums);
                this.setKeys(validKeys);
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    updateQuorum = (quorumKey: number) => {
        this.votingBasis.quorum = quorumKey;
    };

    updateKey = (keyId: string) => {
        this.votingBasis.key1 = keyId;
    };

    updateVeto = (veto: boolean) => {
        this.votingBasis.veto = veto;
    };

    updateVotingBasisField = <K extends keyof IVotingBasis>(field: K, value: IVotingBasis[K]) => {
        this.votingBasis[field] = value;
    };

    resetVotingBasis = () => {
        this.votingBasis = {
            votingId: "",
            title: "",
            question: "",
            quorum: QuorumType.SIMPLE_MAJORITY,
            key1: "",
            key2: "",
            veto: false,
            votingMlsId: "",
            votetype: VoteType.ARBITRAMENT
        };
    };

    resetAllVotingData = () => {
        this.resetVotingBasis();
        this.setVotingOptionsDefault([i18next.t("labels.yes"), i18next.t("labels.no"), i18next.t("labels.abstrain")]);
        this.setSelectedRealestateIds([]);
    };

    saveVotingInformationBasis = async () => {
        if (this.votingBasis.votingId.length <= 0) {
            this.updateVotingBasisField("votingId", uuidv4());
        }

        const insertBody: InsertVotingInformationBasisVariables = {
            objects: [
                {
                    id: this.votingBasis.votingId,
                    key1id: this.votingBasis.key1 || null,
                    key2id: this.votingBasis.key2 || null,
                    quorum: this.votingBasis.quorum,
                    veto: this.votingBasis.veto,

                    voting_mls: {
                        data: [
                            {
                                id: this.votingBasis.votingMlsId || uuidv4(),
                                title: this.votingBasis.title,
                                question: this.votingBasis.question,
                                language: i18next.language
                            }
                        ],
                        on_conflict: {
                            constraint: survey_voting_mls_constraint.PK_voting_mls,
                            update_columns: [
                                survey_voting_mls_update_column.title,
                                survey_voting_mls_update_column.question
                            ]
                        }
                    }
                }
            ]
        };

        try {
            await apolloClientInstance.mutate<InsertVotingInformationBasis, InsertVotingInformationBasisVariables>({
                mutation: INSERT_VOTING_INFORMATION_BASIS,
                variables: insertBody
            });
        } catch (error: any) {
            console.error(i18next.t("screens.assemblies.vote.voting_save_failed: ", error));
        }
    };

    saveVotingOptions = async () => {
        const votingId = this.votingBasis.votingId;
        const votingOptions = this.isNewVoting ? this.votingOptionsDefault : this.votingOptions.map((opt) => opt.text);
        const selectedRealestateIds = this.selectedRealestateIds;

        const agendaitemId = this.rootStore.agendaitemStore.currentAgendaItem?.id;
        const assemblyId = this.rootStore.assemblyStore.currentAssembly?.id;

        runInAction(() => {
            this.saveVotetypeVoting();
        });

        if (!votingId || !agendaitemId || !assemblyId) {
            console.error("Missing required data: votingId, agendaitemId or assemblyId");
            return;
        }

        const insertBody = {
            votingid: votingId,
            objectVotingOptions: votingOptions.map((text) => ({
                votingid: votingId,
                votingoption_mls: {
                    data: [
                        {
                            text,
                            language: i18next.language || "de"
                        }
                    ]
                }
            })),
            objectVotingAgendaitems: [
                {
                    votingid: votingId,
                    agendaitemid: agendaitemId,
                    assemblyid: assemblyId,
                    votingagendaitemrealestates: {
                        data: selectedRealestateIds.map((realestateid) => ({ realestateid }))
                    }
                }
            ]
        };

        try {
            await apolloClientInstance.mutate<InsertVotingOptions, InsertVotingOptionsVariables>({
                mutation: INSERT_VOTING_OPTION_AGENDAITEM,
                variables: insertBody
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_save_success"),
                MessageType.SUCCESS
            );

            runInAction(() => {
                this.loadAllVotingsByAgendaItemId(agendaitemId);
                // set the currentagendaitem -> hasVotings true where agendaitemId
            });
        } catch (error: any) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_save_failed"),
                MessageType.ERROR
            );
            console.error(i18next.t("screens.assemblies.vote.voting_save_failed: ", error));
            this.deleteVotingById(this.votingBasis.votingId);
        }
        this.resetAllVotingData();
    };

    saveVotetypeVoting = async () => {
        const votingId = this.votingBasis.votingId;
        const votetype = this.votingBasis.votetype;

        if (!votingId) {
            console.error("Missing required data: votingId");
            return;
        }

        try {
            await apolloClientInstance.mutate<UpdateVotetypeVotings, UpdateVotetypeVotingsVariables>({
                mutation: UPDATE_VOTETYPE_VOTINGS,
                variables: {
                    id: votingId,
                    votetype: votetype
                }
            });
        } catch (error: any) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_save_failed"),
                MessageType.ERROR
            );
            console.error(i18next.t("screens.assemblies.vote.voting_save_failed: ", error));
            this.deleteVotingById(this.votingBasis.votingId);
        }
        this.resetAllVotingData();
    };

    deleteVotingById = async (votingid: string) => {
        try {
            const idToDelete = votingid ?? this.votingBasis.votingId;

            if (idToDelete !== undefined) {
                await apolloClientInstance.mutate<DeleteVoting, DeleteVotingVariables>({
                    mutation: DELETE_VOTING,
                    variables: { votingId: idToDelete }
                });

                runInAction(() => {
                    this.votingList = this.votingList.filter((voting) => voting.votingid !== idToDelete);
                });
            }
        } catch (error: any) {
            console.error("Voting failed to delete. Error: ", error);
        }
    };

    saveVotingResult = async () => {
        const votingOptions = this.votingOptions;
        const responses = this.votingComponentResponse;

        if (this.votingResults.length === 0 || votingOptions.length === 0) {
            console.error("Missing voting results or voting options.");
            return;
        }

        const updatedResults = this.votingResults.map((result) => {
            // Find the response for this tenant
            const response = responses.find((r) => r.id === result.tenantid);

            let votingoptionid: string | null = null;

            if (response && response.selectedColumn !== null) {
                const index = response.selectedColumn - 1;
                votingoptionid = votingOptions[index]?.votingoptionid ?? null;
            }

            return {
                ...result,
                votingoptionid
            };
        });

        const insertBody = {
            votingagendaitemresults: updatedResults
        };

        try {
            await apolloClientInstance.mutate<UpsertVotingAgendaitemResult, UpsertVotingAgendaitemResultVariables>({
                mutation: UPSERT_VOTING_AGENDA_RESULTS,
                variables: insertBody
            });

            runInAction(() => {
                this.votingResults = updatedResults;
                this.votingComponentResponse = [];
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_result_success"),
                MessageType.SUCCESS
            );
        } catch (error: any) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_result_failed"),
                MessageType.ERROR
            );
            console.error("Error setTenantVotingResult: ", error);
        }
    };

    /* SETTERS */

    setIsVotingModalDisplayed = (isVotingModalDisplayed: boolean) => {
        this.isVotingModalDisplayed = isVotingModalDisplayed;
    };

    setQuorums = (quorums: IQuorums[]) => {
        this.quorums = quorums;
    };

    setKeys = (keys: IKeys[]) => {
        this.keys = keys;
    };

    setVotingOptionsDefault = (votingOptionsDefault: string[]) => {
        this.votingOptionsDefault = votingOptionsDefault;
    };

    setVotingOptions = (votingOptions: IVotingOptions[]) => {
        this.votingOptions = votingOptions;
    };

    setSelectedRealestateIds = (selectedRealestateIds: string[]) => {
        this.selectedRealestateIds = selectedRealestateIds;
    };

    setSelectedVoting = (selectedVoting: IVoting) => {
        if (this.selectedVoting.votingid !== selectedVoting.votingid) {
            this.selectedVoting = selectedVoting;
        }
    };

    setVotingComponentResponse = (response: { id: string; selectedColumn: number | null }[]) => {
        this.votingComponentResponse = response;
    };

    setIsNewVoting = (isNewVoting: boolean) => {
        this.isNewVoting = isNewVoting;
    };

    setSelectedVotingId = (selectedVotingId: string) => {
        this.selectedVotingId = selectedVotingId;
    };

    setQuorumAndKeysMls = (quorum: number, key1: string, key2?: string) => {
        this.quorumMls = this.quorums.find((q) => q.key === quorum)?.label || "";
        this.key1Mls = this.keys.find((k) => k.keyid === key1)?.name || "";
        this.key2Mls = key2 ? this.keys.find((k) => k.keyid === key2)?.name || "" : "";
    };

    setIsVotingResultTie = (isVotingResultTie: boolean) => {
        this.isVotingResultTie = isVotingResultTie;
    };
}
